import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import type { DataTablePaginationProps } from '../types';

import { Button } from '@/components/ui/button';
import { inputVariants } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

import { cn } from '@/lib/utils';

import { CLIENT_PAGE_TABLES_PAGE_SIZES } from '@/constants/pagination';

export const DataTablePagination = (props: DataTablePaginationProps) => {
  const {
    page,
    sizePerPage,
    recordsTotal,
    onPageChange,
    onSizePerPageChange,
    showInfo = true,
    pageSizes = CLIENT_PAGE_TABLES_PAGE_SIZES,
  } = props;
  const { t } = useTranslation();

  const start = (page - 1) * sizePerPage + 1;
  const end = Math.min(page * sizePerPage, recordsTotal);
  const hasNext = page * sizePerPage < recordsTotal;
  const hasPrev = page > 1;

  const onSizePerPageChangeInternal = (value: string) => {
    const newSize = parseInt(value, 10);
    if (onSizePerPageChange) {
      onSizePerPageChange(newSize);
    }
  };

  const sizeSelect = (
    <Select
      value={sizePerPage.toString()}
      onValueChange={onSizePerPageChangeInternal}
    >
      <SelectTrigger
        aria-label={t('Rows Per Page')}
        className={cn(
          inputVariants({ kind: 'trigger', size: 'sm', height: 'forced' }),
          'w-18 gap-1',
        )}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {pageSizes.map((size) => (
          <SelectItem
            key={size}
            value={size.toString()}
          >
            {size}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  const rangeInfo = showInfo && (
    <span className="text-muted-foreground font-medium tabular-nums">
      {start} - {end} {t('Of')} {recordsTotal}
    </span>
  );

  return (
    <div className="flex flex-col gap-2 text-xs md:flex-row md:items-center md:justify-end md:gap-3">
      <div className="flex gap-2 md:hidden">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(page - 1)}
          disabled={!hasPrev}
          className="flex-1"
        >
          <ChevronLeft />
          {t('Previous')}
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(page + 1)}
          disabled={!hasNext}
          className="flex-1"
        >
          {t('Next')}
          <ChevronRight />
        </Button>
      </div>
      <div className="flex items-center justify-between gap-3 md:justify-end">
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground">{t('Rows Per Page')}</span>
          {sizeSelect}
        </div>
        {rangeInfo}
      </div>
      <div className="hidden items-center gap-1 md:flex">
        <Button
          variant="outline"
          size="icon-sm"
          aria-label={t('Previous')}
          onClick={() => onPageChange(page - 1)}
          disabled={!hasPrev}
        >
          <ChevronLeft />
        </Button>
        <Button
          variant="outline"
          size="icon-sm"
          aria-label={t('Next')}
          onClick={() => onPageChange(page + 1)}
          disabled={!hasNext}
        >
          <ChevronRight />
        </Button>
      </div>
    </div>
  );
};
